import { parseDateOnly, parseDateTime, toDateOnly } from '@agroone/dates'

import { AgrooneNutrient } from './agroone-nutrient'
import { Crop } from './crop'
import { ForeignMaterial } from './foreign-material'
import { Location } from './location'
import { MechanicalWeeding } from './mechanical-weeding'
import { Notification } from './notification'
import { Product } from './product'
import { ReportingDAO } from './reporting'
import { SaveRecommendation } from './save-recommendation'
import { ScoutingDisease, ScoutingForeignMaterial, ScoutingInsect, ScoutingWeed } from './scouting'

export class RecommendationPlanting {
  varietyId: number | undefined
  varietyName?: string
  lotNumber?: string
  seedingRate: number | undefined
  seedingRateUnit: string | undefined
  seedingArea: number | undefined
  seedingAreaUnit: string | undefined
  targetDate: string
  depthOfPlanting?: number | null
  depthOfPlantingUnits?: string | null
  fieldBuffer?: string
  rolledAfterSeed?: boolean
  createdAt?: string
  updatedAt?: string
  createdBy?: number
  updatedBy?: number

  get targetDateAsDate(): Date | undefined {
    return parseDateTime(this.targetDate)
  }

  constructor(recommendationPlanting?: Omit<RecommendationPlanting, 'targetDateAsDate'>) {
    this.lotNumber = recommendationPlanting?.lotNumber ?? ''
    this.targetDate = recommendationPlanting?.targetDate ?? ''
    this.seedingRate = recommendationPlanting?.seedingRate
    this.seedingRateUnit = recommendationPlanting?.seedingRateUnit
    this.seedingArea = recommendationPlanting?.seedingArea
    this.seedingAreaUnit = recommendationPlanting?.seedingAreaUnit
    this.targetDate = recommendationPlanting?.targetDate ?? ''
    this.depthOfPlanting = recommendationPlanting?.depthOfPlanting
    this.depthOfPlantingUnits = recommendationPlanting?.depthOfPlantingUnits
    this.fieldBuffer = recommendationPlanting?.fieldBuffer
    this.rolledAfterSeed = recommendationPlanting?.rolledAfterSeed
    this.varietyName = recommendationPlanting?.varietyName
    this.varietyId = recommendationPlanting?.varietyId
  }
}

export class RecommendationIrrigation {
  id?: number
  startingDate: string
  endingDate: string
  area: number
  amount: number
  areaUnit: string
  amountUnit: string
  nutrients?: AgrooneNutrient[]

  get startingDateAsDate(): Date | undefined {
    return parseDateOnly(this.startingDate) ?? undefined
  }

  get endingDateAsDate(): Date | undefined {
    return parseDateOnly(this.endingDate) ?? undefined
  }

  constructor(recommendationIrrigation?: Omit<RecommendationIrrigation, 'startingDateAsDate' | 'endingDateAsDate'>) {
    this.startingDate = recommendationIrrigation?.startingDate ?? toDateOnly(new Date())
    this.endingDate = recommendationIrrigation?.endingDate ?? toDateOnly(new Date())
    this.area = recommendationIrrigation?.area ?? 0
    this.amount = recommendationIrrigation?.amount ?? 0
    this.areaUnit = recommendationIrrigation?.areaUnit ?? ''
    this.amountUnit = recommendationIrrigation?.amountUnit ?? ''
    this.nutrients = recommendationIrrigation?.nutrients
  }
}

export class RecommendationOtherOperation {
  id?: number
  operationType: string
  area: string | undefined
  areaUnit: string | undefined | null
  targetDate: string | null
  supplierId?: number | null
  supplierIsFarm?: boolean

  get targetDateAsDate(): Date | undefined | null {
    return parseDateTime(this.targetDate)
  }

  constructor(recommendationOtherOperation?: Omit<RecommendationOtherOperation, 'targetDateAsDate'>) {
    this.area = recommendationOtherOperation?.area
    this.areaUnit = recommendationOtherOperation?.areaUnit
    this.targetDate = recommendationOtherOperation?.targetDate ?? ''
    this.operationType = recommendationOtherOperation?.operationType ?? ''
    this.supplierId = recommendationOtherOperation?.supplierId
    this.supplierIsFarm = recommendationOtherOperation?.supplierIsFarm
  }
}

export class RecommendationFoodSafety {
  id?: number
  location?: Location
  bufferZone?: number
  bufferZoneUnit?: string
  completedDate?: string

  constructor(recommendationFoodSafety?: RecommendationFoodSafety) {
    if (recommendationFoodSafety?.location) {
      this.location = recommendationFoodSafety?.location
    }
    if (recommendationFoodSafety?.bufferZone) {
      this.bufferZone = recommendationFoodSafety?.bufferZone
    }
    if (recommendationFoodSafety?.bufferZoneUnit) {
      this.bufferZoneUnit = recommendationFoodSafety?.bufferZoneUnit
    }
  }
}

export class RecommendationScouting {
  id!: number
  foreignMaterial?: ScoutingForeignMaterial | null
  weed?: ScoutingWeed | null
  insect?: ScoutingInsect | null
  disease?: ScoutingDisease | null
  note?: string
  cropId!: number | null
  region?: string | null
}

export class RecommendationCrop {
  id!: number
  name!: string
  shortName!: string
  lastGrowthStage?: string | null
  region?: string
}

export class Recommendation {
  static readonly className: string = 'Recommendation'

  id?: number
  date: string | undefined
  createdBy?: number
  createdAt?: string
  crops: RecommendationCrop[] | undefined
  doneBy?: number
  doneByName?: string
  farmId?: number
  foodSafety?: RecommendationFoodSafety
  foreignMaterial?: ForeignMaterial
  irrigation?: RecommendationIrrigation
  mechanicalWeeding?: MechanicalWeeding
  note?: string
  notifications?: Notification[]
  otherOperation?: RecommendationOtherOperation
  pesticides?: RecommendationPesticides
  picturesPath?: string[]
  planting?: RecommendationPlanting
  recommendedBy: number | undefined
  recommendedByName?: string
  region: string | undefined
  scoutings?: number[] | RecommendationScouting[]
  sendEmail?: boolean
  sender?: string
  sendSMS?: boolean
  signature?: string
  subType: string | null | undefined
  traceabilityId?: number
  type: string | undefined
  updatedBy?: number
  updatedAt?: string

  constructor(recommendation?: Recommendation) {
    this.id = recommendation?.id
    this.date = recommendation?.date
    this.recommendedBy = recommendation?.recommendedBy
    this.recommendedByName = recommendation?.recommendedByName
    this.doneBy = recommendation?.doneBy
    this.doneByName = recommendation?.doneByName
    this.region = recommendation?.region
    this.type = recommendation?.type
    this.subType = recommendation?.subType
    this.note = recommendation?.note
    this.signature = recommendation?.signature
    this.sendSMS = recommendation?.sendSMS
    this.sendEmail = recommendation?.sendEmail
    this.sender = recommendation?.sender
    this.crops = recommendation?.crops
    this.scoutings = recommendation?.scoutings
    this.notifications = recommendation?.notifications
    this.foodSafety = recommendation?.foodSafety
    this.planting = recommendation?.planting
    this.mechanicalWeeding = recommendation?.mechanicalWeeding
    this.otherOperation = recommendation?.otherOperation
    this.pesticides = recommendation?.pesticides
    this.irrigation = recommendation?.irrigation
    this.foreignMaterial = recommendation?.foreignMaterial
    this.picturesPath = recommendation?.picturesPath
    this.traceabilityId = recommendation?.traceabilityId
    this.farmId = recommendation?.farmId
  }
}

export class RecommendationDAO extends ReportingDAO {
  id?: number
  date!: string
  recommendedBy!: number
  crops?: RecommendationCrop[]
  region?: string
  type!: string
  subType!: string
  note?: string
  signature?: string
  sendSMS?: number
  sendEmail?: number
  traceabilityId?: number
  sender?: string
  foodSafetyId?: number
  otherOperationId?: number
  irrigationId?: number
  mechanicalWeedingId?: number
  pesticideId?: number
  plantingId?: number
  farmId?: number
  regionId: number | undefined
  scoutingId?: number
  foreignMaterialId?: number
  doneBy?: number | null

  constructor(recommendation: SaveRecommendation) {
    super(recommendation)
  }
}

export class RecommendationRequest {
  id?: number
  date!: string
  recommendedBy!: number
  recommendedByFirstname!: string
  recommendedByLastname!: string
  crops?: RecommendationCrop[]
  region?: string
  scoutings?: RecommendationScouting[]
  type!: string
  subType!: string
  note?: string
  signature?: string
  sendSMS?: number
  sendEmail?: number
  traceabilityId?: number
  sender?: string
  farmId?: number
  regionId!: number
  mechanicalWeedingId?: number
  aopId?: number
  aopWaterAmount?: number
  aopWaterUnit?: string
  aopWaterAreaUnit!: string
  aopTargetDate!: string
  aopCompletedDate?: string
  aopAlternative?: string
  aopSupplierId?: number
  aopSupplierName?: string
  aopSupplierType?: string
  aopSupplierContactIds?: string
  aopSupplierContactLastNames?: string
  aopSupplierContactFirstNames?: string
  aopSupplierContactPriorities?: string
  aopSupplierContactActives?: string
  aopSupplierContactEmailAddresses?: string
  aopSupplierContactPhoneNumbers?: string
  pIds?: string
  pRates?: string
  pRateUnits?: string
  pRateAreaUnits?: string
  pIdentificationNumbers?: string
  pNames?: string
  pPesticideTypes?: string
  pArea?: string
  pTargetDate?: string
  pAlternativeSolution!: string
  pNote?: string
  cropIds?: string
  cropNames?: string
  cropShortNames?: string
  cropGrowthStages?: string
  cropIntendedAreas?: string
  cropAreaUnits?: string
  cropPlantedAreas?: string
  cropPlantedDates?: string
  cropFieldIds?: string
  cropLatitudes?: string
  cropLongitudes?: string
  cropPrecisions?: string
  cropGeohashs?: string
  cropBoundaries?: string
  notifIds?: string
  notifContactType?: string
  notifSendSms?: string
  notifSendEmail?: string
  scoutingIds?: string
  scoutingNotes?: string
  cropScoutingIds?: string
  scoutingWeedItem?: string
  scoutingWeedArea?: string
  scoutingWeedPressure?: string
  scoutingWeedStage?: string
  scoutingDiseaseItem?: string
  scoutingDiseaseArea?: string
  scoutingDiseasePressure?: string
  scoutingFMaterialType?: string
  scoutingFMaterialSubType?: string
  scoutingFMaterialArea?: string
  scoutingFMaterialLevel?: string
  scoutingInsectItem?: string
  scoutingInsectArea?: string
  scoutingInsectPressure?: string
  scoutingInsectStage?: string
  activeIngredientsNames?: string
  picturePaths?: string
  fsId?: number
  fsBufferZone?: number
  fsBufferZoneUnit?: string
  fsCompletedDate?: string
  fsLatitude?: string
  fsLongitude?: string
  fsPrecision?: string
  fsGeohash?: string
  iId?: number
  iStartingDate?: string
  iEndingDate?: string
  iArea?: number
  iAmount?: number
  iAreaUnit?: string
  iAmountUnit?: string
  nIds?: string
  nKeys?: string
  nTypes?: string
  nValues?: string
  nNutrientIds?: string
  mwId?: number
  mwArea?: string
  mwAreaUnit?: string
  mwTargetDate?: string
  ooId?: number
  ooOperationType?: string
  ooArea?: string
  ooAreaUnit?: string
  ooTargetDate?: string
  ooSupplierId?: number
  ooSupplierIsFarm?: boolean
  fmId?: number
  fmType?: string
  fmSubType?: string
  fmArea?: string
  fmCompletedDate!: string
  fmLevel!: string
  vaVarietyName?: string
  plVarietyId?: number
  plSeedingRate?: number
  plSeedingRateUnit?: string
  plSeedingArea?: number
  plSeedingAreaUnit?: string
  plTargetDate?: string
  plLotNumber?: string
  plDepthOfPlanting?: number
  plDepthOfPlantingUnits?: string
  createdBy?: number
  doneBy?: number
}

export class RecommendationPesticides {
  id?: number
  products?: Product[]
  waterAmount: number
  waterUnit: string
  waterAreaUnit: string
  completedDate?: string | null
  supplierId?: number | null
  supplierIsFarm?: boolean
  supplier?: RecommendationPesticidesSupplier

  constructor(
    recommendationPesticides?: Omit<RecommendationPesticides, 'targetDateAsDate' | 'products'> & {
      products: Partial<Product>[]
    },
  ) {
    this.waterAmount = +(recommendationPesticides?.waterAmount ?? 0)
    this.waterUnit = recommendationPesticides?.waterUnit ?? ''
    this.waterAreaUnit = recommendationPesticides?.waterAreaUnit ?? ''
    this.supplierId = recommendationPesticides?.supplierId
    this.supplierIsFarm = recommendationPesticides?.supplierIsFarm
    const products: Product[] | undefined = recommendationPesticides?.products.map((p) => new Product(p))

    if (products) {
      this.products = products
    }
    if (recommendationPesticides?.completedDate) {
      this.completedDate = recommendationPesticides?.completedDate
    }

    if (recommendationPesticides?.supplier) {
      this.supplier = new RecommendationPesticidesSupplier(recommendationPesticides?.supplier)
      this.supplierId = recommendationPesticides?.supplier.id
    }
  }

  static getEarliestTargetDate(products: Product[] | undefined): Date | null {
    const targetDates: Date[] = (products ?? [])
      .map((product) => parseDateOnly(product.targetDate))
      .filter((date): date is Date => date !== null)

    if (targetDates.length === 0) {
      return null
    }

    return targetDates.reduce((earliest, date) => (date < earliest ? date : earliest))
  }
}

export class RecommendationPesticidesSupplier {
  id: number | undefined
  name: string | undefined
  contacts?: SupplierContact[]

  constructor(recommendationPesticidesSupplier?: RecommendationPesticidesSupplier) {
    this.id = recommendationPesticidesSupplier?.id
    this.name = recommendationPesticidesSupplier?.name
    if (recommendationPesticidesSupplier?.contacts && recommendationPesticidesSupplier?.contacts.length) {
      this.contacts = recommendationPesticidesSupplier?.contacts
    }
  }
}

export class SupplierContact {
  id: number | undefined
  name: string | undefined
  priority: number | undefined
  active: boolean | undefined
  emailAddress: string | undefined
  phoneNumbers: string[] | undefined

  constructor(supplierContact?: SupplierContact) {
    this.id = supplierContact?.id
    this.name = supplierContact?.name
    this.priority = supplierContact?.priority
    this.active = supplierContact?.active
    this.emailAddress = supplierContact?.emailAddress
    this.phoneNumbers = supplierContact?.phoneNumbers
  }
}

export class RecommendationContact {
  contactType!: string
  email!: string
  language!: string
  crops!: Crop[];
  [key: string]: any
}

export enum Sender {
  'FRANCE SOUTH WEST' = 'SOLEAL',
}
